import { useState } from "react";
import { FieldPath, FieldValues, useFormContext } from "react-hook-form";

import { useGetDocumentConfigQuery } from "src/core/api/documents";
import { useDocumentPreview } from "src/core/contexts/DocumentPreviewContext";
import { Document } from "src/core/types/document";
import { validateDocument } from "src/core/utils/documentValidation";

export const useDocumentUploadFieldset = <
  T extends FieldValues & { documents: Record<string, Document> },
>(
  requiredDocuments: {
    code: string;
    label: string;
  }[]
) => {
  const form = useFormContext<T>();
  const [fileErrors, setFileErrors] = useState<Record<string, string>>({});
  const { data: documentConfig } = useGetDocumentConfigQuery();
  const { handlePreviewDocument } = useDocumentPreview();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, code: string) => {
    const file = event.target.files?.[0];
    if (file) {
      const validation = validateDocument(file, documentConfig);
      if (validation.isValid) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        form.setValue(`documents.${code}.file` as FieldPath<T>, file as any);
        setFileErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[code];
          return newErrors;
        });
      } else {
        setFileErrors((prev) => ({ ...prev, [code]: validation.error || "" }));
        event.target.value = "";
      }
    }
  };

  const deleteDoc = (index: number) => {
    const docCode = requiredDocuments[index].code;

    const currentValues = form.getValues();
    form.reset({
      ...currentValues,
      documents: {
        ...currentValues.documents,
        [docCode]: {
          file: null,
          existing: false,
          fileId: undefined,
          fileName: undefined,
          expirationDate: undefined,
        },
      },
    });
  };

  return { form, handleFileChange, deleteDoc, fileErrors, handlePreviewDocument };
};
